import {
  isAssetReadOnly,
  useCurrentAccount,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AddIcon,
  Button,
  CardButton,
  Colors,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { isActivityOrEditPage } from '../assetUtils';
import { AddAssetMediaType, AddFilesLinksSheet } from './addFilesLinksButton';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

type AssetMediaFoldProps = {
  workspaceAsset: WorkspaceAsset | undefined;
  assetId: string;
  forceShowing?: boolean;
};

export const AssetMediaFold: React.FC<AssetMediaFoldProps> = ({
  workspaceAsset,
  assetId,
  forceShowing,
}) => {
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');
  const { t } = useTranslation();
  const mediaData = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );
  const displayMediaData = useMemo(() => mediaData.slice(0, 3), [mediaData]);
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();
  const slideLayout = useSlideLayout();
  const { prepareSlideTransition } = slideLayout;
  // basePath and issueId come from the nearest AssetRouteProvider — set by every
  // component that renders AssetCard (both left and right columns).
  const { basePath, issueId } = useAssetRouting(assetId);
  const skipNextSlide = isActivityOrEditPage(location);
  const url = `${basePath}/files`;
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const asset = workspaceAsset?.asset;

  // Picking a type slides into the dedicated `files/new` panel — the sheet no
  // longer opens an upload Modal of its own.
  const handleSelectType = useCallback(
    (type: AddAssetMediaType) => {
      setIsAddSheetOpen(false);
      if (!skipNextSlide) {
        prepareSlideTransition(SlideAction.NEXT);
      }
      navigate(`${url}/new?type=${type}`, {
        state: !skipNextSlide
          ? { slideLayoutInitialTransition: SlideAction.NEXT }
          : undefined,
      });
    },
    [navigate, prepareSlideTransition, skipNextSlide, url]
  );

  useEffect(() => {
    if (!slideLayout || !asset || isReadOnlyMode) return;
    if (!isAddSheetOpen) {
      slideLayout.setColumnOverlay(null);
      return;
    }
    slideLayout.setColumnOverlay(
      <AddFilesLinksSheet
        isOpen={isAddSheetOpen}
        onClose={() => setIsAddSheetOpen(false)}
        onSelectType={handleSelectType}
      />
    );
    return () => slideLayout.setColumnOverlay(null);
  }, [slideLayout, asset, isReadOnlyMode, isAddSheetOpen, handleSelectType]);

  const handleFilePress = useCallback(
    (fileId: string) => {
      if (!skipNextSlide) {
        prepareSlideTransition(SlideAction.NEXT);
      }

      navigate(`${url}/${fileId}`, {
        state: {
          source: 'assetCard',
          ...(!skipNextSlide
            ? { slideLayoutInitialTransition: SlideAction.NEXT }
            : {}),
        },
      });
    },
    [navigate, url, prepareSlideTransition, skipNextSlide]
  );

  const handleViewAllFiles = useCallback(() => {
    if (!skipNextSlide) {
      prepareSlideTransition(SlideAction.NEXT);
    }

    navigate(
      `${url}/all`,
      !skipNextSlide
        ? { state: { slideLayoutInitialTransition: SlideAction.NEXT } }
        : undefined
    );
  }, [navigate, url, prepareSlideTransition, skipNextSlide]);

  if (!workspaceAsset || (!forceShowing && issueId && isOnAssetPage))
    return null;

  return (
    <View
      style={[
        styles.containerHeightCropCircle,
        !displayMediaData.length
          ? { height: 100 }
          : { height: displayMediaData.length * 60 + 32 },
      ]}
    >
      <ViewAllSectionHeader
        title={t('asset.files.title')}
        onPress={handleViewAllFiles}
      />
      {displayMediaData.length > 0 ? (
        <View style={[styles.listContainer]}>
          <FlatList
            data={displayMediaData}
            keyExtractor={(item, index) =>
              item.id?.toString() || index.toString()
            }
            scrollEnabled={false}
            renderItem={({ item }) => {
              const mediaId = item.id?.toString();

              return (
                <View style={styles.cardButtonContainer}>
                  <CardButton
                    text={item.name}
                    color="white"
                    leftIcon="Document B"
                    rightIcon="Chevron Right"
                    showAvatars={false}
                    onPress={
                      mediaId ? () => handleFilePress(mediaId) : undefined
                    }
                    bordered={false}
                  />
                </View>
              );
            }}
            showsVerticalScrollIndicator={true}
          />
        </View>
      ) : isReadOnlyMode ? null : (
        <Button
          style={styles.addFileEmpty}
          onPress={() => setIsAddSheetOpen(true)}
          leftIcon={<AddIcon />}
          variant={'rectangular'}
        >
          {t('asset.files.addFile')}
        </Button>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeightCropCircle: {
    maxHeight: 262,
    minHeight: 80,
    height: '100%',
    marginBottom: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  listContainer: {
    maxHeight: 184,
    overflow: 'hidden',
    marginBottom: 54,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: Colors.role.surfaceRaised,
  },
  cardButtonContainer: {
    marginBottom: tokens.spacing.sm,
  },
});
